/**
 * AuthGuard — global auth redirect-loop protection.
 *
 * Tracks consecutive auth failures in sessionStorage.
 * If failures exceed MAX_FAILURES within the session, clears all tokens,
 * signs out, and redirects to "/" with a toast message.
 */
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const MAX_FAILURES = 3;
const FAILURE_KEY = "auth_failure_count";
const PROTECTED_PATTERNS = ["/dashboard", "/provider-dashboard", "/admin", "/supplier/", "/franchisee-dashboard", "/d/", "/a/", "/s/", "/c/"];

function isProtectedRoute(path: string) {
  return PROTECTED_PATTERNS.some(p => path.startsWith(p));
}

export function recordAuthFailure() {
  const count = parseInt(sessionStorage.getItem(FAILURE_KEY) ?? "0") + 1;
  sessionStorage.setItem(FAILURE_KEY, String(count));
  return count;
}

export function clearAuthFailures() {
  sessionStorage.removeItem(FAILURE_KEY);
}

const AuthGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const count = parseInt(sessionStorage.getItem(FAILURE_KEY) ?? "0");
    if (count >= MAX_FAILURES) {
      // Loop detected — hard reset
        (async () => {
        clearAuthFailures();
        // Remove only auth keys — preserve booking drafts and other app data
        const AUTH_KEYS = [
          "authToken",
          "supabase.auth.token",
          "sb-jhejytgbcyvbepnvdosu-auth-token",
          "sb-access-token",
          "sb-refresh-token",
        ];
        AUTH_KEYS.forEach(key => localStorage.removeItem(key));
        sessionStorage.removeItem("auth_failure_count");
        await supabase.auth.signOut();
        navigate("/", { replace: true });
        toast.error("Your session expired or authentication failed. Please log in again.");
      })();
      return;
    }

    // On protected routes without a session, count as failure
    if (isProtectedRoute(location.pathname)) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) {
          const newCount = recordAuthFailure();
          if (newCount >= MAX_FAILURES) {
            clearAuthFailures();
            navigate("/", { replace: true });
            toast.error("Your session expired or authentication failed. Please log in again.");
          }
        } else {
          // Successful auth on protected route — reset failure counter
          clearAuthFailures();
        }
      });
    }
  }, [location.pathname, navigate]);

  return null;
};

export default AuthGuard;
